import React, { FunctionComponent, useMemo } from 'react';
import RoutesRenderer from 'core/routing/routes-renderer';
import DashboardLayout from 'core/layout/dashboard';
import { COMPONENT_ROUTING, ISSUER_NAVIGATION, ROUTES } from './routes.config';
import { useIssuerNavigationItems } from './hooks';
import { useServerConfigSelector } from 'core/config/hooks';
import { hasManageTriggerSolutionPermission } from '../helpers/tokenPermissions';
import { useCurrentUserSelector } from 'core/auth/hooks';
import {
  hasDistributionManageVouchersPermission,
  hasDistributionViewFriendReferralPermission,
} from '../helpers/distributionPlatformPermissions';
import {
  currentUserHasCreateIssuerPermission,
  currentUserHasManageOrganizationsPermission,
  currentUserHasManageUsersAndGroupsPermission,
} from 'apps/issuer/helpers/userPermissions';
import {
  hasCreateIssuerPermissionOnAtLeastOneOrganization,
  hasManageUsersAndGroupsOnAtLeastOneOrganization,
} from '../helpers/organizationPermissions';

export interface IssuerPagesProps {
  hasIssuersToView: boolean;
}

const IssuerPages: FunctionComponent<IssuerPagesProps> = ({ hasIssuersToView }) => {
  const { config } = useServerConfigSelector();
  const { currentUser } = useCurrentUserSelector();

  const routes = useIssuerNavigationItems({
    currentUser: currentUser,
    hasIssuersToView,
    configFeatures: config.features,
    distributionPlatforms: currentUser?.associatedDistributionPlatforms || [],
    environmentPermissions: currentUser?.permissions || [],
    canCreateProducts: currentUser?.canCreateProducts || false,
  });

  let filteredRoutes = ISSUER_NAVIGATION;

  if (!hasManageTriggerSolutionPermission(currentUser)) {
    filteredRoutes = filteredRoutes.filter((nav) => nav.key !== ROUTES.triggerSolutionsSettlements);
  }

  if (
    !currentUserHasCreateIssuerPermission(currentUser?.permissions || []) &&
    !hasIssuersToView &&
    !hasCreateIssuerPermissionOnAtLeastOneOrganization(currentUser)
  ) {
    filteredRoutes = filteredRoutes.filter((nav) => nav.key !== ROUTES.issuers);
  }

  if (!hasDistributionManageVouchersPermission(currentUser?.associatedDistributionPlatforms || [])) {
    filteredRoutes = filteredRoutes.filter((nav) => nav.key !== ROUTES.vouchers);
  }

  if (!hasDistributionViewFriendReferralPermission(currentUser?.associatedDistributionPlatforms || [])) {
    filteredRoutes = filteredRoutes.filter((nav) => nav.key !== ROUTES.friendReferrals);
  }

  if (!currentUserHasManageOrganizationsPermission(currentUser?.permissions || [])) {
    filteredRoutes = filteredRoutes.filter((nav) => nav.key !== ROUTES.organizations);
  }

  if (
    !currentUserHasManageUsersAndGroupsPermission(currentUser?.permissions || []) &&
    !hasManageUsersAndGroupsOnAtLeastOneOrganization(currentUser)
  ) {
    filteredRoutes = filteredRoutes.filter((nav) => nav.key !== ROUTES.permissions);
  }

  if (
    !currentUserHasManageUsersAndGroupsPermission(currentUser?.permissions || []) &&
    !hasManageUsersAndGroupsOnAtLeastOneOrganization(currentUser) &&
    !currentUserHasManageOrganizationsPermission(currentUser?.permissions || [])
  ) {
    filteredRoutes = filteredRoutes.filter((nav) => nav.key !== ROUTES.administration);
  }

  const navItems = useMemo(
    () => (routes ? filteredRoutes.filter((nav) => !!routes[nav.key]) : []),
    [JSON.stringify(routes)],
  );

  return (
    <DashboardLayout navigationItems={navItems}>
      <RoutesRenderer routes={routes} componentRouting={COMPONENT_ROUTING} />
    </DashboardLayout>
  );
};

export default IssuerPages;
